import { versionDate, versionRowPresentation } from 'src/components/star-grid/utils/versionPickerPresentation';
import { isIcebergVersion, isVisibleVersion } from 'src/components/star-grid/utils/versionAvailability';
import { PnlDataVersion, SnapshotDateRange, VersionTreeItem } from 'src/components/star-grid/reportSelectors.types';
import { matchesSnapshotDateRange } from 'src/components/star-grid/utils/versionDateFilter';
import { HIDDEN_UNTAGGED_SNAPSHOT_ID } from 'src/components/star-grid/versionCatalogConstants';

const timestamp = (version: PnlDataVersion): number => versionDate(version).valueOf() || 0;

export const liveVersion = (versions: readonly PnlDataVersion[]): PnlDataVersion | undefined =>
  versions.find((version) => ['live', 'current'].includes(version.version.toLowerCase()) && version.as_of === null) ??
  versions.find((version) => version.as_of === null && version.resolvable !== false && !version.snapshot_id);

const isSnapshot = (version: PnlDataVersion): boolean => isIcebergVersion(version) && versionDate(version).isValid();

const isCatalogVersionVisible = (version: PnlDataVersion): boolean =>
  isVisibleVersion(version) && !(isIcebergVersion(version) && version.snapshot_id === HIDDEN_UNTAGGED_SNAPSHOT_ID);

const versionItem = (version: PnlDataVersion): VersionTreeItem => {
  const presentation = versionRowPresentation(version);
  return { ...presentation, id: `version:${version.version}`, label: presentation.displayLabel, version };
};

const matchesVersion = (version: PnlDataVersion, terms: readonly string[]): boolean => {
  const date = versionDate(version);
  const searchable = [
    version.label,
    version.version,
    version.snapshot_id,
    version.as_of,
    date.isValid() ? date.format('MMMM MMM YYYY-MM-DD HH:mm:ss z') : ''
  ]
    .join(' ')
    .toLowerCase();
  return terms.every((term) => searchable.includes(term));
};

const groupSnapshots = (versions: readonly PnlDataVersion[], format: string): VersionTreeItem[] => {
  const groups = new Map<string, PnlDataVersion[]>();
  versions.forEach((version) => {
    const key = versionDate(version).format(format);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(version);
  });
  return Array.from(groups, ([key, group]) => ({
    id: `snapshot:${key}`,
    label: format === 'YYYY' ? key : versionDate(group[0]).format('MMMM'),
    children: format === 'YYYY' ? groupSnapshots(group, 'YYYY-MM') : group.map(versionItem)
  }));
};

const matchingHistory = (versions: readonly PnlDataVersion[], query: string, range?: SnapshotDateRange): PnlDataVersion[] => {
  const live = liveVersion(versions);
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  return versions
    .filter(
      (version) => version !== live && isCatalogVersionVisible(version) && matchesVersion(version, terms) && matchesSnapshotDateRange(version, range)
    )
    .sort((a, b) => timestamp(b) - timestamp(a));
};

export const buildVersionCatalog = (versions: readonly PnlDataVersion[], query: string, range?: SnapshotDateRange) => {
  const live = liveVersion(versions);
  const history = matchingHistory(versions, query, range);
  const snapshots = history.filter(isSnapshot);
  const tagged = history.filter((version) => !isSnapshot(version)).map(versionItem);
  const snapshotGroups = groupSnapshots(snapshots, 'YYYY');
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  return {
    live: live ? versionItem(live) : undefined,
    hasMatches: history.length > 0 || Boolean(live && matchesVersion(live, terms)),
    items: [...tagged, ...snapshotGroups],
    tagged,
    snapshotGroups,
    snapshots: snapshots.map(versionItem),
    totalSnapshots: versions.filter((version) => isCatalogVersionVisible(version) && isSnapshot(version)).length
  };
};

export const expandedVersionGroups = (items: readonly VersionTreeItem[], selected?: string, isSearching = false): string[] =>
  items.flatMap((item) => {
    if (!item.children) return [];
    const children = expandedVersionGroups(item.children, selected, isSearching);
    const containsSelection = item.children.some((child) => child.version?.version === selected);
    return isSearching || children.length || containsSelection ? [item.id, ...children] : [];
  });

export const versionHistoryItems = (items: readonly VersionTreeItem[], heading: string): readonly VersionTreeItem[] => {
  const firstSnapshotGroup = items.findIndex((item) => Boolean(item.children));
  if (firstSnapshotGroup < 0) return items;
  return [
    ...items.slice(0, firstSnapshotGroup),
    { id: 'snapshot-history-heading', label: heading, isHeading: true },
    ...items.slice(firstSnapshotGroup)
  ];
};
